import { useMemo } from "react";
import {
	useMattersCache,
	useCaseListCache,
	useCacheMutations,
	type Matter,
} from "@/shared/cache";
import { useApi } from "@/shared/utils/request";
import { matterLookupText } from "@/shared/utils/matterLabels";

export type MyMattersActionResult = {
	ok: boolean;
	error?: string;
	/** Set when the matter is already on the list (add is a no-op). */
	already?: boolean;
};

/**
 * Shared data + mutations for the "My Matters" feature (the signed-in
 * professional's case list). Consumed by both the v2 settings page and the v1
 * SettingsPanel section. Components own their own UI state (toasts, dialogs).
 */
export function useMyMatters() {
	const { matters, isLoading: mattersLoading } = useMattersCache();
	const { caseListIds, isLoading: linksLoading } = useCaseListCache();
	const { refresh } = useCacheMutations();
	const { makeApiRequest } = useApi();

	const linkedMatters = useMemo(
		() =>
			matters
				.filter((m) => caseListIds.has(m.matter_id))
				.sort((a, b) =>
					(a.description || matterLookupText(a)).localeCompare(
						b.description || matterLookupText(b),
					),
				),
		[matters, caseListIds],
	);

	// Linked matter_ids with no matching row in the matters cache (e.g. closed or
	// status-filtered matters). Still surfaced so they can be removed.
	const unknownLinkedIds = useMemo(() => {
		const known = new Set(matters.map((m) => m.matter_id));
		return [...caseListIds].filter((id) => !known.has(id)).sort();
	}, [matters, caseListIds]);

	const addMatter = async (matterId: string): Promise<MyMattersActionResult> => {
		if (caseListIds.has(matterId)) return { ok: false, already: true };
		const resp = await makeApiRequest({
			endpoint: "/v2/matters-professionals",
			method: "POST",
			body: { matterId },
		});
		if (resp.status === "ERROR") {
			return { ok: false, error: resp.error ?? "Failed to add matter" };
		}
		await refresh("case_list");
		return { ok: true };
	};

	const removeMatter = async (
		matterId: string,
	): Promise<MyMattersActionResult> => {
		// makeApiRequest only auto-appends queryParams for GET, so build it inline.
		const resp = await makeApiRequest({
			endpoint: `/v2/matters-professionals?matterId=${encodeURIComponent(matterId)}`,
			method: "DELETE",
		});
		if (resp.status === "ERROR") {
			return { ok: false, error: resp.error ?? "Failed to remove matter" };
		}
		await refresh("case_list");
		return { ok: true };
	};

	return {
		matters,
		caseListIds,
		linkedMatters,
		unknownLinkedIds,
		linkedCount: linkedMatters.length + unknownLinkedIds.length,
		isLoading: mattersLoading || linksLoading,
		addMatter,
		removeMatter,
	};
}

export type { Matter };
